<template>
  <section :class="['attachments-panel', { 'attachments-panel--compact': compact }]">
    <div v-if="showHeader" class="attachments-panel__header">
      <div>
        <p class="attachments-panel__eyebrow">Contenido</p>
        <h3 class="attachments-panel__title">Archivos adjuntos</h3>
      </div>
      <div class="d-flex align-center">
        <v-btn
          v-if="attachments.length > 0"
          @click="downloadAllFiles"
          class="mr-2"
          small
          icon
          title="Descargar todos los archivos"
        >
          <v-icon>mdi-download-multiple</v-icon>
        </v-btn>
        <v-btn v-if="showClose" @click="$emit('close')" class="mt-1 mr-4 mr-md-0" x-small icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <v-divider v-if="showHeader" class="mb-0"></v-divider>

    <div class="attachments-panel__content">
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mr-2"
        ></v-progress-circular>
        <span class="loading-text">Cargando archivos...</span>
      </div>

      <div v-else-if="attachments.length === 0" class="text-center py-8">
        <p class="text-muted">No hay archivos adjuntos</p>
      </div>

      <div v-else>
        <div
          v-for="(attachment, index) in paginatedAttachments"
          :key="attachment.id || attachment.file_path || index"
          class="attachment-item mb-3"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-file</v-icon>
            <div class="flex-grow-1">
              <a :href="attachment.file_path" target="_blank" class="attachment-link">
                {{ attachment.original_name }}
              </a>
              <div class="attachment-meta">
                <span class="text-muted">{{ formatFileSize(attachment.file_size) }}</span>
                <span class="text-muted mx-2">-</span>
                <span class="text-muted">{{ formatDate(attachment.created_at) }}</span>
              </div>
            </div>
            <v-btn
              @click="downloadFile(attachment)"
              small
              icon
              class="ml-2"
              title="Descargar archivo"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="showPagination" class="attachments-pagination">
          <span class="attachments-pagination__meta">
            {{ paginationLabel }}
          </span>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            total-visible="5"
            color="#0076ff"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import moment from 'moment';
import JSZip from 'jszip';
moment.locale('es');

export default {
  name: 'ContentAttachments',
  props: {
    active: {
      type: Boolean,
      default: true,
    },
    lessonId: {
      type: [Number, String],
      required: true,
    },
    scheduleId: {
      type: [Number, String],
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      attachments: [],
      loading: false,
      downloadingZip: false,
      currentPage: 1,
      attachmentsPerPage: 5,
    };
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.attachments.length / this.attachmentsPerPage));
    },
    paginatedAttachments() {
      const start = (this.currentPage - 1) * this.attachmentsPerPage;
      return this.attachments.slice(start, start + this.attachmentsPerPage);
    },
    showPagination() {
      return this.attachments.length > this.attachmentsPerPage;
    },
    paginationLabel() {
      if (this.attachments.length === 0) return '0 de 0';

      const from = ((this.currentPage - 1) * this.attachmentsPerPage) + 1;
      const to = Math.min(from + this.paginatedAttachments.length - 1, this.attachments.length);
      return `${from}-${to} de ${this.attachments.length}`;
    },
  },

  watch: {
    active(newVal) {
      if (newVal) {
        this.currentPage = 1;
        this.loadAttachments();
      }
    },
    lessonId() {
      if (this.active) this.loadAttachments();
    },
    scheduleId() {
      if (this.active) this.loadAttachments();
    },
  },

  mounted() {
    if (this.active) {
      this.loadAttachments();
    }
  },

  methods: {
    async loadAttachments() {
      try {
        this.loading = true;
        const response = await this.$store.dispatch('profile/LIST_LESSONS_ATTACHMENTS', {
          lesson_id: this.lessonId,
          schedule_id: this.scheduleId,
        });
        this.attachments = response.data.data;
        this.currentPage = 1;
      } catch (error) {
        console.error('Error cargando archivos:', error);
        this.$snotify.error('Error al cargar los archivos adjuntos');
      } finally {
        this.loading = false;
      }
    },

    downloadFile(attachment) {
      window.open(attachment.file_path, '_blank');
    },

    async downloadAllFiles() {
      if (this.downloadingZip) return;

      try {
        this.downloadingZip = true;
        this.$snotify.info('Preparando archivo ZIP con todos los adjuntos...', 'Descarga');

        const zip = new JSZip();
        const downloadPromises = this.attachments.map(async (attachment) => {
          console.log('attachment', attachment);
          try {
            const response = await fetch(attachment.file_path);
            const blob = await response.blob();
            zip.file(attachment.original_name, blob);
            return true;
          } catch (error) {
            console.error(`Error descargando ${attachment.original_name}:`, error);
            return false;
          }
        });

        const results = await Promise.all(downloadPromises);
        const successCount = results.filter(result => result).length;

        if (successCount === 0) {
          this.$snotify.error('No se pudo descargar ningun archivo');
          return;
        }

        const content = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = `archivos_adjuntos_${this.lessonId}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$snotify.success(`Se descargaron ${successCount} de ${this.attachments.length} archivos`, 'Descarga completada');
      } catch (error) {
        console.error('Error creando el archivo ZIP:', error);
        this.$snotify.error('Error al crear el archivo ZIP');
      } finally {
        this.downloadingZip = false;
      }
    },

    formatDate(date) {
      return moment(date).format('DD MMM YYYY HH:mm');
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
  },
};
</script>

<style scoped>
.attachments-panel {
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 8px;
}

.attachments-panel--compact {
  height: auto;
  min-height: 0;
  border-radius: 0;
}

.attachments-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 14px;
  background: #fff;
}

.attachments-panel__eyebrow {
  margin: 0 0 2px;
  color: #8a98a5;
  font: normal normal 400 12px/18px Poppins;
}

.attachments-panel__title {
  margin: 0;
  font: normal normal 700 20px/28px Poppins;
  color: #101820;
}

.attachments-panel__content {
  flex: 1;
  overflow-y: auto;
  padding: 18px 24px 24px;
  background: #fff;
}

.attachments-panel--compact .attachments-panel__content {
  padding: 0;
  overflow: visible;
}

.attachment-item {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #edf1f5;
  transition: all 0.3s ease;
}

.attachment-item:hover {
  background: #e9f7fb;
}

.attachment-link {
  color: #0076ff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  word-break: break-word;
}

.attachment-link:hover {
  text-decoration: underline;
}

.attachment-meta {
  font-size: 12px;
  margin-top: 4px;
}

.text-muted {
  color: #6c757d;
}

.attachments-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.attachments-pagination__meta {
  color: #6b7785;
  font: normal normal 400 12px/18px Poppins;
}

.attachments-pagination >>> .v-pagination {
  justify-content: flex-end;
}

.attachments-pagination >>> .v-pagination__item,
.attachments-pagination >>> .v-pagination__navigation {
  min-width: 30px;
  height: 30px;
  box-shadow: none;
}

@media (max-width: 600px) {
  .attachments-pagination {
    justify-content: center;
  }
}
</style>
